import { Platform } from 'react-native';
import { Prompt, ResponseType } from 'expo-auth-session';
import * as WebBrowser from 'expo-web-browser';
import environment from '../environments';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';
import { addDays, addSeconds, format } from 'date-fns';
import { api } from '../api/api';
import { log } from './LogHelper';
import { stringify } from 'flatted';
import { useUserContext } from '../context/UserContext';
import { useUpdateUser } from '../hooks/user/useUpdateUser';
import {
  OuraSpecificDataType,
  UserAttributeSource,
  UserAttributeType
} from '../models/UserAttribute';
import { logAnalyticsEvent } from '../common/analytics';
import { useDeviceSyncContext } from '../context/DeviceSyncContext';
import { displayedWearableConnection } from './WearableConnection';

export interface OuraSleepData {
  id: string;
  average_breath: number;
  average_hrv: number;
  awake_time: number;
  bedtime_end: string;
  bedtime_start: string;
  day: string;
  deep_sleep_duration: number;
  efficiency: number;
  hrv: {
    interval: number;
    items: number[];
    timestamp: string;
  };
  latency: number;
  light_sleep_duration: number;
  lowest_heart_rate: number;
  readiness: {
    temperature_deviation: number;
    temperature_trend_deviation: number;
  };
  rem_sleep_duration: number;
  time_in_bed: number;
  total_sleep_duration: number;
  type: string;
}

export interface OuraHrvData {
  average_hrv: number;
  hrv: {
    interval: number;
    items: number[];
    timestamp: string;
  };
  day: string;
}

export interface OuraStepsData {
  active_calories: number;
  high_activity_time: number;
  low_activity_time: number;
  medium_activity_time: number;
  non_wear_time: number;
  sedentary_time: number;
  steps: number;
  day: string;
  timestamp: string;
}

export interface OuraHearRateData {
  bpm: number;
  source: string;
  timestamp: string;
}

export interface OuraSpo2Data {
  day: string;
  spo2_percentage: {
    average: number;
  };
  breathing_disturbance_index: number;
}

export interface OuraWorkoutData {
  activity: string;
  intensity: string;
  start_datetime: string;
  end_datetime: string;
  calories: number;
}

export type OuraAuthProps = {
  clientId: string;
  redirectUri: string;
};

export const getOuraAuthRequestConfig = () => {
  return {
    prompt: Prompt.Login,
    responseType: ResponseType.Token,
    clientId: environment.ouraAppClientId,
    redirectUri:
      Platform.OS !== 'web'
        ? environment.oAuthRedirectAppUri
        : environment.oAuthRedirectWebUri,
    scopes: [
      'personal',
      'daily',
      'heartrate',
      'spo2',
      'workout',
      'ring_configuration'
    ]
  };
};

export const ouraDiscovery = {
  authorizationEndpoint: 'https://cloud.ouraring.com/oauth/authorize'
};

export const getOuraTokenExpiration = async () => {
  return await AsyncStorage.getItem('ouraTokenExpiration');
};

export async function revokeOuraAuthorization(
  userId: number,
  clientId: string
): Promise<void> {
  try {
    const result = await api.post(`oura/${userId}/revoke`, { clientId });
    const response = await result.response;
    log(`revokeOuraAuthorization results:`, response ? 'success' : 'failed');
  } catch (error) {
    log(`revokeOuraAuthorization error:`, stringify(error));
    throw error;
  }
}

export async function fetchOuraData(
  type: UserAttributeType | OuraSpecificDataType,
  userId: number,
  startDate?: Date
): Promise<any | undefined> {
  const getFormattedDateString = (date: Date) => {
    return format(date, 'yyyy-MM-dd');
  };
  const result = await api.get(`oura/${userId}/data`, {
    type,
    startDate: startDate
      ? getFormattedDateString(startDate)
      : getFormattedDateString(addDays(new Date(), -30)),
    endDate: getFormattedDateString(addDays(new Date(), 1))
  }).response;
  const response = await result.json();
  if (response.error) {
    logAnalyticsEvent('fetch_oura_data_error', {
      userId: userId,
      message: response.message
    });
  }
  return result.ok ? response : { error: true, ...response };
}

export async function syncOuraData(userId: number): Promise<any | undefined> {
  const result = await api.post(`oura/${userId}/sync`, {}).response;
  const response = await result.json();
  if (response.error) {
    logAnalyticsEvent('sync_oura_data_error', {
      userId: userId,
      message: response.message
    });
    throw response;
  }
  return response;
}

export function useOura() {
  const { user, updateUserSession, setUser } = useUserContext();
  const { updateUser } = useUpdateUser();
  const [loading, setLoading] = useState<boolean>();
  const [ouraAccessToken, setOuraAccessToken] = useState<string | undefined>();
  const config = getOuraAuthRequestConfig();
  const { synchronizeData } = useDeviceSyncContext();
  const [fromConnect, setFromConnect] = useState<boolean>();

  const connectionToken = displayedWearableConnection(user)?.ouraAccessToken;

  useEffect(() => {
    if (fromConnect && connectionToken) {
      synchronizeData(UserAttributeSource.Oura);
      setFromConnect(false);
    }
  }, [connectionToken, fromConnect, synchronizeData]);

  const ouraAuthUrl =
    ouraDiscovery.authorizationEndpoint +
    `?response_type=${config.responseType}` +
    `&scope=${config.scopes.join('+')}` +
    `&client_id=${config.clientId}` +
    `&redirect_uri=${encodeURIComponent(config.redirectUri)}` +
    `&prompt=${config.prompt}`;

  const handleOuraAuthResult = useCallback(
    async (event: any) => {
      setLoading(true);
      if (event?.url) {
        const params = new URLSearchParams(event.url.split('#')[1]);
        const accessToken = params.get('access_token');
        const expiresIn = params.get('expires_in');
        if (user && accessToken && expiresIn) {
          setFromConnect(true);
          const updated = await updateUser({ ouraAccessToken: accessToken });
          if (updated) {
            setUser(updated);
          } else {
            setUser({
              ...user,
              wearableConnection: {
                ...(displayedWearableConnection(user) || {
                  userId: user.id
                }),
                ouraAccessToken: accessToken
              }
            });
          }
          setOuraAccessToken(accessToken);
          AsyncStorage.setItem(
            'ouraTokenExpiration',
            addSeconds(new Date(), +expiresIn).getTime().toString()
          );
          setLoading(false);
          return accessToken;
        }
      }
      setLoading(false);
      return undefined;
    },
    [setUser, updateUser, user]
  );
  const connectOura = useCallback(async () => {
    setLoading(true);
    const authSessionResult = await WebBrowser.openAuthSessionAsync(
      ouraAuthUrl,
      undefined,
      { preferEphemeralSession: true }
    );
    const result = await handleOuraAuthResult(authSessionResult);
    return result;
  }, [handleOuraAuthResult, ouraAuthUrl]);

  const disconnectOura = useCallback(
    async (userId: number) => {
      setLoading(true);
      try {
        if (Platform.OS === 'web') {
          const logoutWindow = window.open(
            'https://cloud.ouraring.com/v2/account/logout',
            '_blank'
          );
          if (logoutWindow) {
            // set as 100ms to prevent display of unauthorized error text display
            // before closing logout tab if user has connected oura on mobile
            // and tried to disconnect on web without an oura session. this
            // also takes into consideration slow connection as 10ms timeout
            // is enough to logout from oura before closing the logout tab.
            setTimeout(() => {
              logoutWindow.close();
            }, 100);
          } else {
            console.error('error');
          }
        }
        await revokeOuraAuthorization(userId, config.clientId);
        updateUserSession();
      } catch (e) {
        alert(e);
      }
      setOuraAccessToken(undefined);
      setLoading(false);
    },
    [config.clientId, updateUserSession]
  );

  useEffect(() => {
    setOuraAccessToken(
      displayedWearableConnection(user)?.ouraAccessToken || undefined
    );
  }, [user]);

  return {
    loading,
    ouraAccessToken,
    ouraAuthUrl,
    handleOuraAuthResult,
    connectOura,
    disconnectOura
  };
}
